import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Sparkles, ArrowRight } from 'lucide-react';
import { fetchPersonalStats } from '../../lib/api';

/**
 * StreakBanner — banner emotivo in cima alla Dashboard.
 *
 * Comportamento:
 *   - Streak ≥ 1 settimane: mostra fiamma 🔥 e count, CTA "Mantieni il ritmo".
 *   - Streak 0 + ALMENO 1 KR on-track: mostra micro-cta "Chiudi una MIT oggi
 *     per iniziare lo streak".
 *   - Stato vuoto (0 KR attivi): hidden.
 *   - Errore di fetch: hidden (degrade gracefully).
 *
 * Dimensioni: striscia orizzontale leggera, colore verde se streak, ambra
 * se "inizia". Linka sempre a /stats.
 */
export default function StreakBanner() {
  const [stats, setStats] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    fetchPersonalStats()
      .then(setStats)
      .catch(() => setHidden(true));
  }, []);

  if (hidden || !stats) return null;

  const streak = stats.streak_weeks || 0;
  const krActive = stats.kr?.total_active || 0;
  const closedMonth = stats.mit?.closed_this_month || 0;

  // Nessun KR attivo → non distrarre, niente banner
  if (krActive === 0) return null;

  // Streak in corso
  if (streak >= 1) {
    return (
      <Link
        to="/stats"
        className="block mb-8 group"
        data-testid="streak-banner"
        data-streak={streak}
      >
        <div className="flex items-center justify-between gap-4 px-5 py-3 border border-ok/30 bg-ok/10 hover:bg-ok/15 transition-colors">
          <div className="flex items-center gap-3 min-w-0">
            <Flame size={18} strokeWidth={1.5} className="text-ok shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-base text-ink truncate">
                Streak di <strong>{streak}</strong> {streak === 1 ? 'settimana' : 'settimane'} consecutive con almeno 1 MIT chiusa
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ok/80 mt-0.5">
                {closedMonth > 0
                  ? `${closedMonth} MIT chiuse questo mese · mantieni il ritmo`
                  : 'mantieni il ritmo, basta 1 MIT a settimana'}
              </div>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-ok group-hover:gap-2 transition-all">
            Statistiche <ArrowRight size={12} strokeWidth={1.8} />
          </span>
        </div>
      </Link>
    );
  }

  // Streak 0 → CTA gentile per iniziare
  return (
    <Link
      to="/stats"
      className="block mb-8 group"
      data-testid="streak-banner"
      data-streak="0"
    >
      <div className="flex items-center justify-between gap-4 px-5 py-3 border border-amber-200 bg-amber-50/60 hover:bg-amber-50 transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <Sparkles size={18} strokeWidth={1.5} className="text-amber-700 shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-base text-amber-900 truncate">
              Inizia il tuo streak: chiudi anche solo <strong>1 MIT</strong> questa settimana
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-700/80 mt-0.5">
              ogni settimana con ≥1 MIT chiusa estende lo streak
            </div>
          </div>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-amber-700 group-hover:gap-2 transition-all">
          Statistiche <ArrowRight size={12} strokeWidth={1.8} />
        </span>
      </div>
    </Link>
  );
}
